import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, fmtXof, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Offres() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [payingId, setPayingId] = useState("");
  const [momoPkg, setMomoPkg] = useState(null);
  const [momoOperator, setMomoOperator] = useState("Orange Money");
  const [momoPhone, setMomoPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/credits/packages").then(({ data }) => setPackages(data)).catch(() => {});
    if (user) api.get("/credits/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, [user]);

  const currentIdx = wallet?.package_name ? packages.findIndex((p) => p.name === wallet.package_name) : -1;

  const buyStripe = async (pkg) => {
    if (!user) { toast.error("Connectez-vous pour acheter une offre"); navigate("/auth"); return; }
    setPayingId(pkg.id);
    try {
      const { data } = await api.post("/credits/purchase/stripe", { package_id: pkg.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setPayingId(""); }
  };

  const submitMomo = async () => {
    if (!momoPhone.trim()) { toast.error("Numéro requis"); return; }
    setBusy(true);
    try {
      await api.post("/credits/purchase/momo", { package_id: momoPkg.id, operator: momoOperator, phone: momoPhone });
      toast.success("Offre activée — vos unités sont créditées.");
      setMomoPkg(null); setMomoPhone("");
      navigate("/compte");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12" data-testid="offres-page">
      <p className="font-mono text-xs uppercase tracking-widest text-ciel">Offres & unités de temps</p>
      <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-navy sm:text-5xl">Choisissez votre formule</h1>
      <p className="mt-3 max-w-2xl text-base text-slate-600">Achetez des unités de temps (minutes) à utiliser pour vos téléconsultations et prestations. Passez à une formule supérieure quand vous le souhaitez.</p>

      {user && wallet && (
        <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="offres-current">
          <div className="flex items-center gap-2 text-navy"><Clock size={18} /><span className="font-semibold">Formule actuelle : {wallet.package_name || "Aucune"}</span></div>
          {wallet.package_name && (
            <span className="font-mono text-sm text-slate-500">{wallet.units_remaining} / {wallet.units_total} unités restantes{wallet.expires_at ? ` · expire le ${new Date(wallet.expires_at).toLocaleDateString("fr-FR")}` : ""}</span>
          )}
        </div>
      )}

      <div className="mt-8 grid gap-6 md:grid-cols-3">
        {packages.map((pkg, idx) => {
          const isCurrent = wallet?.package_name === pkg.name;
          const isUpgrade = currentIdx >= 0 && idx > currentIdx;
          return (
            <div key={pkg.id} data-testid={`offre-card-${pkg.id}`}
              className={`flex flex-col rounded-2xl border-2 bg-white p-6 shadow-sm transition ${idx === 1 ? "border-ciel" : "border-slate-200"}`}>
              {idx === 1 && <span className="mb-2 w-fit rounded-full bg-ciel px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Populaire</span>}
              <h3 className="font-display text-2xl font-bold text-navy">{pkg.name}</h3>
              <p className="mt-1 text-3xl font-bold text-navy">{fmtXof(pkg.price)}</p>
              <p className="mt-1 font-mono text-sm text-ciel">{pkg.units} unités · {pkg.validity_days} jours</p>
              <ul className="mt-4 flex-1 space-y-2">
                {(pkg.benefits || []).map((b, k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-slate-600"><Check size={15} className="mt-0.5 shrink-0 text-emerald-500" />{b}</li>
                ))}
              </ul>
              {isCurrent ? (
                <div className="mt-5 rounded-full bg-emerald-50 py-2.5 text-center text-sm font-semibold text-emerald-700" data-testid={`offre-current-${pkg.id}`}>Votre formule actuelle</div>
              ) : (
                <div className="mt-5 space-y-2">
                  <Button onClick={() => buyStripe(pkg)} disabled={payingId === pkg.id} data-testid={`offre-stripe-${pkg.id}`}
                    className="w-full rounded-full bg-navy text-white hover:bg-navy-light">
                    {payingId === pkg.id ? <Loader2 className="animate-spin" size={16} /> : (<>{isUpgrade ? "Upgrader mon offre" : "Choisir"} <ArrowRight size={15} className="ml-1" /></>)}
                  </Button>
                  <Button onClick={() => { if (!user) { toast.error("Connectez-vous pour acheter"); navigate("/auth"); return; } setMomoPkg(pkg); }} variant="outline" data-testid={`offre-momo-${pkg.id}`}
                    className="w-full rounded-full border-amber-400 text-amber-700 hover:bg-amber-50">Mobile Money</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!momoPkg} onOpenChange={() => { setMomoPkg(null); setMomoPhone(""); }}>
        <DialogContent data-testid="offre-momo-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Paiement Mobile Money</DialogTitle></DialogHeader>
          {momoPkg && <p className="text-sm text-slate-500">Offre <span className="font-semibold">{momoPkg.name}</span> — <span className="font-mono font-semibold text-navy">{fmtXof(momoPkg.price)}</span></p>}
          <select value={momoOperator} onChange={(e) => setMomoOperator(e.target.value)} data-testid="offre-momo-operator" className="mt-3 w-full rounded-md border border-slate-300 p-2.5 text-sm">
            <option>Orange Money</option><option>MTN Mobile Money</option><option>Moov Money</option><option>Wave</option>
          </select>
          <input value={momoPhone} onChange={(e) => setMomoPhone(e.target.value)} placeholder="Numéro de téléphone" data-testid="offre-momo-phone" className="mt-2 w-full rounded-md border border-slate-300 p-2.5 text-sm" />
          <Button onClick={submitMomo} disabled={busy} data-testid="offre-momo-submit" className="mt-2 w-full rounded-full bg-amber-500 text-white hover:bg-amber-600">{busy ? "Traitement…" : "Confirmer le paiement"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
